import type { ItemDaLista, ItemDaReposicao } from '../../giro';

/**
 * Regras puras do Giro do balconista (telas 19–21), fora do componente para serem testadas.
 * Nenhuma decide gate, EAN ou estoque — isso é do servidor. Aqui só se escolhe a PRÓXIMA TELA e se
 * resume o que o servidor já devolveu.
 */

export type PassoDaContagem =
  | { readonly t: 'contar'; readonly itemId: string }
  | { readonly t: 'foto'; readonly itemId: string }
  | { readonly t: 'resumo' };

/**
 * Onde continua quem abre (ou avança) a contagem: primeiro o próximo produto sem contar; depois
 * um produto contado cuja foto de auditoria ficou para trás (saiu da tela, a rede caiu no envio);
 * só então o resumo. Sem o passo da foto, o resumo recusa ("falta a foto") e não havia tela para
 * tirá-la — o balconista ficava preso no dia.
 *
 * `pular` é o item que acabou de ser resolvido nesta tela: a lista em mãos pode ser de antes.
 */
export function proximoPassoDaContagem(
  itens: readonly Pick<ItemDaLista, 'id' | 'situacao'>[],
  pular: string | null = null,
): PassoDaContagem {
  const pendente = itens.find((i) => i.situacao === 'pendente' && i.id !== pular);
  if (pendente) return { t: 'contar', itemId: pendente.id };
  const semFoto = itens.find((i) => i.situacao === 'foto_pendente' && i.id !== pular);
  if (semFoto) return { t: 'foto', itemId: semFoto.id };
  return { t: 'resumo' };
}

/**
 * Depois de a contagem ser CONFIRMADA pelo servidor (Carlos e dono, 29/09): o próximo produto
 * abre direto — sem a tela "Contagem salva / Repor X" no meio e sem abrir a câmera. A reposição
 * continua calculada e salva no servidor e aparece no resumo e na fila. Só a foto sorteada segura
 * o item (regra de auditoria, não muda).
 */
export function depoisDeContar(
  itens: readonly Pick<ItemDaLista, 'id' | 'situacao'>[],
  itemId: string,
  r: { readonly fotoExigida: boolean },
): PassoDaContagem {
  if (r.fotoExigida) return { t: 'foto', itemId };
  return proximoPassoDaContagem(itens, itemId);
}

/**
 * O código com que o toque na lista identifica o produto no setor que exige EAN (dono, 29/09:
 * "tocar já identifica" — o servidor registra como ENTRADA MANUAL, o auditor vê que não foi
 * escaneado). Sem código nenhum, só a câmera identifica: null.
 */
export function codigoParaIdentificar(i: Pick<ItemDaLista, 'codigo' | 'ean'>): string | null {
  return i.codigo?.trim() || i.ean?.trim() || null;
}

/**
 * O leitor aberto pela lista pode ler QUALQUER produto pendente do bloco, não só o próximo: acha
 * o item pelo EAN (ou código interno). Nenhum bate: fica o esperado — o servidor responde
 * "produto diferente" ou "não reconhecido", como antes.
 */
export function itemDoCodigoLido(
  itens: readonly Pick<ItemDaLista, 'id' | 'situacao' | 'ean' | 'codigo'>[],
  codigo: string,
  esperado: string,
): string {
  const c = codigo.trim();
  const achado = itens.find((i) => i.situacao === 'pendente' && (i.ean === c || i.codigo === c));
  return achado?.id ?? esperado;
}

/** Atalhos da contagem (Carlos, 29/09): 0 (prateleira vazia) e 1 a 6; o resto é "Digitar". */
export const ATALHOS_DE_QUANTIDADE: readonly number[] = [0, 1, 2, 3, 4, 5, 6];

/** A linha abaixo do nome na busca manual: fabricante · código — nunca "null"/"undefined". */
export function linhaDoProduto(fabricante: string | null, codigo: string | null): string {
  const fab = fabricante?.trim() ? fabricante.trim() : 'Fabricante não informado';
  return codigo?.trim() ? `${fab} · cód. ${codigo.trim()}` : fab;
}

/**
 * O fim do Giro: "Reposições" são os produtos em que alguma unidade foi para a prateleira — quem
 * respondeu "não achei nenhuma" (reposto 0) está nas diferenças, não nas reposições.
 */
export function resumoDaReposicao(
  itens: readonly Pick<ItemDaReposicao, 'repor' | 'encontrado' | 'reposto'>[],
): { repostos: number; unidades: number; diferencas: number } {
  return {
    repostos: itens.filter((i) => (i.reposto ?? 0) > 0).length,
    unidades: itens.reduce((a, i) => a + (i.reposto ?? 0), 0),
    diferencas: itens.filter((i) => (i.encontrado ?? 0) < i.repor).length,
  };
}

/** A última leitura do scanner que voltou do servidor sem confirmar o produto. */
export interface UltimaLeitura {
  readonly codigo: string;
  /** Quando a resposta chegou (ms). */
  readonly em: number;
  /** Falha de envio (recusada/incerta): o MESMO código só vai de novo quando a pessoa tocar. */
  readonly aguardaToque: boolean;
}

/** Tempo em que o mesmo código, ainda na frente da câmera, é ignorado. */
export const PAUSA_DO_MESMO_CODIGO_MS = 2000;

/**
 * O leitor vê o código ~4 vezes por segundo. Sem esta regra, depois de "produto diferente" ou de
 * uma falha de rede o mesmo código ia para o servidor a cada 250 ms. Código DIFERENTE passa sempre.
 */
export function podeEnviarLeitura(
  codigo: string,
  ultima: UltimaLeitura | null,
  agora: number,
): boolean {
  if (!ultima || ultima.codigo !== codigo) return true;
  if (ultima.aguardaToque) return false;
  return agora - ultima.em >= PAUSA_DO_MESMO_CODIGO_MS;
}

/**
 * "Entrar no InovaPay": o App só pergunta o gate de novo quando a rota MUDA. Se a URL já é
 * `#/inicio` (o Giro abriu por cima dela), trocar o hash não dispara nada e o botão parece morto —
 * nesse caso a página recarrega para o gate ser perguntado de novo.
 */
export function jaEstaNoInicio(hash: string): boolean {
  return hash.replace(/^#\/?/, '').split('?')[0] === 'inicio';
}
